<template>
  <div class="shop-page">
    <nav_list :index="4"></nav_list>

    <div class="shop-wrap">
      <div v-if="pageLoading" class="skeleton-wrap">
        <div class="sk-hero">
          <div class="sk-sidebar">
            <div class="sk-sidebar-title"></div>
            <div class="sk-sidebar-item" v-for="n in 4" :key="n">
              <div class="sk-sidebar-icon"></div>
              <div class="sk-sidebar-text"></div>
            </div>
          </div>
          <div class="sk-banner"></div>
        </div>
        <div class="sk-section">
          <div class="sk-line-title"></div>
          <div class="sk-grid">
            <div class="sk-card" v-for="n in 4" :key="n">
              <div class="sk-card-cover"></div>
              <div class="sk-card-body">
                <div class="sk-card-title"></div>
                <div class="sk-card-meta"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <template v-else>
        <!-- 搜索栏 -->
        <div class="search-bar-box">
          <div class="search-bar-inner">
            <i class="el-icon-search search-bar-icon"></i>
            <input
              ref="homeSearchInput"
              v-model="searchKeyword"
              class="search-bar-input"
              type="text"
              :placeholder="$lt('搜索商品名称、关键词')"
              @keyup.enter="goSearch"
            />
            <i v-if="searchKeyword" class="el-icon-circle-close search-bar-clear" @click="clearHomeSearch"></i>
            <div class="search-bar-btn" @click="goSearch">{{ $lt('搜索') }}</div>
          </div>
        </div>

        <div class="hero-card" v-if="bannerList.length">
          <el-carousel ref="heroCarousel" class="hero-carousel" :interval="5000" arrow="always" :height="bannerHeight">
            <el-carousel-item
              v-for="(item, index) in bannerList"
              :key="item.id || index"
            >
              <div class="hero-slide" @click="onBannerTap(item)">
                <img :src="item.image" :alt="item.name">
                <div class="hero-slide-overlay">
                  <div class="hero-slide-title">{{ item.name }}</div>
                </div>
              </div>
            </el-carousel-item>
          </el-carousel>

          <div class="hero-sidebar" @click.stop>
            <div class="sidebar-title">
              <i class="el-icon-s-grid"></i>
              <span>{{ $lt('商品分类') }}</span>
            </div>
            <div
              class="sidebar-item"
              v-for="(item, idx) in categoryList"
              :key="item.id"
              @click="onCategoryTap(item)"
            >
              <div class="sidebar-icon">
                <img v-if="item.cover" :src="item.cover" :alt="item.name">
              </div>
              <span class="sidebar-name">{{ getDisplayText(item.name) }}</span>
              <i class="el-icon-arrow-right sidebar-arrow"></i>
            </div>
          </div>
        </div>

        <div class="section-box">
          <div class="line-title">
            <div class="title-container">
              <div class="chinese-title">{{ $lt('当季精选') }}</div>
              <div class="english-title">SEASONAL PICKS</div>
            </div>
          </div>

          <div v-if="recommendList.length" class="recommend-grid">
            <div
              class="recommend-card"
              v-for="item in recommendList"
              :key="item.id"
              @click="onDestinationTap(item)"
            >
              <div class="recommend-cover">
                <img :src="item.image" :alt="item.name">
              </div>
              <div class="recommend-body">
                <div class="recommend-name">{{ item.name }}</div>
                <div class="recommend-meta">
                  <span class="score">★ {{ item.avg_scores }}</span>
                  <span class="price">¥ {{ item.price }}<em>{{ $lt('起') }}</em></span>
                </div>
              </div>
            </div>
          </div>
          <el-empty v-else :description="$lt('暂无推荐商品')"></el-empty>
        </div>

        <div class="shop-body">
          <div class="left-nav">
            <div class="left-nav-inner">
              <div class="left-nav-title">{{ $lt('商品分类') }}</div>
              <div
                class="left-nav-item"
                v-for="item in categoryList"
                :key="item.id"
                @click="onCategoryTap(item)"
              >
                <span>{{ getDisplayText(item.name) }}</span>
                <i>{{ item.products.length }}</i>
              </div>
            </div>
          </div>

          <div class="right-content">
            <div
              class="category-section"
              v-for="category in categoryList"
              :id="'shop-category-' + category.id"
              :key="category.id"
            >
              <div class="category-head">
                <div class="category-title">{{ getDisplayText(category.name) }}</div>
                <div class="category-tools">
                  <div class="filter-toggle" @click="toggleFilter(category.id)">
                    {{ expandedCategories[category.id] ? $lt('收起筛选') : $lt('展开筛选') }}
                  </div>
                </div>
              </div>

              <div v-if="expandedCategories[category.id]" class="child-tabs">
                <div
                  class="child-tab"
                  :class="{ active: activeChildMap[category.id] === -1 }"
                  @click="onChildCategoryTap(category.id, -1)"
                >
                  {{ $lt('全部') }}
                </div>
                <div
                  class="child-tab"
                  v-for="(child, childIndex) in category.children"
                  :key="child.id || childIndex"
                  :class="{ active: activeChildMap[category.id] === childIndex }"
                  @click="onChildCategoryTap(category.id, childIndex)"
                >
                  {{ child.title }}
                </div>
              </div>

              <div v-if="category.productsLoading" class="inner-loading">
                <i class="el-icon-loading"></i>
                <span>{{ $lt('商品加载中...') }}</span>
              </div>

              <div v-else-if="category.products.length" class="product-grid">
                <div
                  class="product-card"
                  v-for="product in category.products"
                  :key="product.id"
                  @click="onProductTap(product)"
                >
                  <div class="product-cover">
                    <img :src="product.image" :alt="product.name">
                  </div>
                  <div class="product-body">
                    <div class="product-name">{{ product.name }}</div>
                    <div class="product-meta">
                      <span class="score">★ {{ product.avg_scores }}</span>
                      <span class="price">¥ {{ product.price }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <el-empty v-else :description="$lt('当前分类暂无商品')"></el-empty>
            </div>
          </div>
        </div>

        <div v-if="loadFailed" class="error-box">
          <span>{{ $lt('首页数据加载失败，请重试') }}</span>
          <el-button type="primary" size="mini" @click="loadHomeConfig">{{ $lt('重新加载') }}</el-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import nav_list from '../index/nav_list.vue';

export default {
  name: 'shop-home',
  components: {
    nav_list,
  },
  data() {
    return {
      searchKeyword: '',
      pageLoading: true,
      loadFailed: false,
      categoryList: [],
      bannerList: [],
      bannerHeight: '675px',
      recommendList: [],
      activeChildMap: {},
      expandedCategories: {},
    };
  },
  mounted() {
    this.loadHomeConfig();
    window.addEventListener('resize', this.updateBannerHeight);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateBannerHeight);
  },
  methods: {
    mapProduct(item) {
      return {
        id: item.prod_no,
        name: item.prod_name,
        image: item.prod_img_url && item.prod_img_url.length ? item.prod_img_url[0] : '',
        price: item.b2c_min_price || '0',
        avg_scores: item.avg_scores || '0',
      };
    },
    mapBanner(item) {
      return {
        id: item.id,
        name: this.getDisplayText(item.paths && item.paths.title ? item.paths.title : item.title),
        image: item.paths && item.paths.cover ? item.paths.cover : '',
        link: item.uni_url || item.url || item.mb_url || '',
      };
    },
    getDisplayText(value, fallback = '') {
      if (value == null) return fallback;
      if (typeof value === 'string' || typeof value === 'number') return String(value);
      if (Array.isArray(value)) {
        return value.map((item) => this.getDisplayText(item, '')).filter(Boolean).join(' / ') || fallback;
      }
      if (typeof value === 'object') {
        return this.getDisplayText(
          value.title || value.name || value.label || value.text || value.desc || value.value,
          fallback,
        );
      }
      return fallback;
    },
    toggleFilter(categoryId) {
      this.$set(this.expandedCategories, categoryId, !this.expandedCategories[categoryId]);
    },
    async loadHomeConfig() {
      this.pageLoading = true;
      this.loadFailed = false;
      try {
        const res = await this.get('/kkday_product/home_config', {}, 'get', false);
        this.bannerList = (res.adv || []).map(this.mapBanner);
        this.recommendList = (res.recommend_products || []).map(this.mapProduct);
        this.categoryList = (res.categories || []).map((item) => ({
          id: item.id,
          name: item.title,
          cover: item.sts && item.sts.cover ? item.sts.cover : '',
          children: item.children || [],
          products: (item.products || []).map(this.mapProduct),
          productsLoading: false,
        }));

        this.categoryList.forEach((category, index) => {
          this.$set(this.activeChildMap, category.id, -1);
          this.$set(this.expandedCategories, category.id, index === 0);
        });

        this.$nextTick(() => {
          this.updateBannerHeight();
        });
      } catch (error) {
        this.loadFailed = true;
        this.$message.error(this.$lt('加载商城首页失败'));
      } finally {
        this.pageLoading = false;
      }
    },
    onCategoryTap(item) {
      this.jump('/shop/category', { id: item.id, name: this.getDisplayText(item.name, this.$lt('商品分类')) });
    },
    async onChildCategoryTap(categoryId, childIndex) {
      this.$set(this.activeChildMap, categoryId, childIndex);
      const category = this.categoryList.find((item) => item.id === categoryId);
      if (!category) {
        return;
      }

      const params = {
        page: 1,
        pageSize: 10,
        cate_id: categoryId,
      };

      if (childIndex !== -1 && category.children[childIndex]) {
        params.cate_sub_id = category.children[childIndex].id;
      }

      category.productsLoading = true;
      try {
        const res = await this.get('/kkday_product/list', params, 'get', false);
        category.products = (res.list || []).map(this.mapProduct);
      } catch (error) {
        category.products = [];
        this.$message.error(this.$lt('加载分类商品失败'));
      } finally {
        category.productsLoading = false;
      }
    },
    onProductTap(product) {
      this.jump('/shop/info', { prod_no: product.id, id: product.id });
    },
    onDestinationTap(item) {
      this.jump('/shop/info', { prod_no: item.id, id: item.id });
    },
    onBannerTap(item) {
      const link = item && item.link ? item.link : '';
      if (!link) return;
      if (/^https?:\/\//.test(link)) {
        window.open(link, '_blank');
        return;
      }
      this.jump(link);
    },
    updateBannerHeight() {
      const heroCarousel = this.$refs.heroCarousel;
      const width = heroCarousel && heroCarousel.$el && heroCarousel.$el.clientWidth ? heroCarousel.$el.clientWidth : 980;
      const bannerWidth = width || 980;
      this.bannerHeight = `${Math.round(bannerWidth * 768 / 1376 * 0.88)}px`;
    },
    goSearch() {
      const kw = this.searchKeyword.trim();
      this.jump('/shop/search', kw ? { keyword: kw } : {});
    },
    clearHomeSearch() {
      this.searchKeyword = '';
    },
  },
};
</script>

<style scoped>
.shop-page {
  min-height: 100vh;
  background: #f5f7fb;
  padding-bottom: 40px;
}

.shop-wrap {
  width: 1200px;
  margin: 20px auto 0;
}

.section-box,
.category-section,
.left-nav-inner,
.error-box {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px 1px rgb(0 0 0 / 6%);
}

/* ===== Search Bar ===== */
.search-bar-box {
  margin-bottom: 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px 1px rgb(0 0 0 / 6%);
  padding: 18px 28px;
}

.search-bar-inner {
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;
}

.search-bar-icon {
  position: absolute;
  left: 16px;
  font-size: 18px;
  color: #bbb;
  z-index: 1;
}

.search-bar-input {
  flex: 1;
  height: 46px;
  padding: 0 44px;
  border: 2px solid #e4e9f0;
  border-radius: 10px;
  font-size: 15px;
  color: #333;
  background: #f9fafb;
  outline: none;
  transition: all 0.25s ease;
}

.search-bar-input:focus {
  border-color: #1c5098;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(28, 80, 152, 0.08);
}

.search-bar-input::placeholder {
  color: #bbb;
}

.search-bar-clear {
  position: absolute;
  right: 120px;
  font-size: 18px;
  color: #bbb;
  cursor: pointer;
  z-index: 1;
  transition: color 0.2s;
}

.search-bar-clear:hover {
  color: #999;
}

.search-bar-btn {
  height: 46px;
  padding: 0 32px;
  background: #1c5098;
  color: #fff;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.2s ease;
  letter-spacing: 0.05em;
}

.search-bar-btn:hover {
  background: #164680;
}

.error-box {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

/* ===== Skeleton ===== */
@keyframes sk-pulse {
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
}

.skeleton-wrap {
  animation: sk-pulse 1.8s ease-in-out infinite;
}

.sk-hero {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  height: 440px;
  background: #e2e8f0;
  margin-bottom: 24px;
}

.sk-sidebar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 260px;
  background: #f1f5f9;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
}

.sk-sidebar-title {
  height: 58px;
  margin: 0 22px;
  border-bottom: 1px solid #e2e8f0;
  border-radius: 6px;
  margin-top: 22px;
  background: #e2e8f0;
}

.sk-sidebar-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 22px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.03);
}

.sk-sidebar-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #e2e8f0;
  flex-shrink: 0;
}

.sk-sidebar-text {
  flex: 1;
  height: 16px;
  border-radius: 4px;
  background: #e2e8f0;
}

.sk-banner {
  position: absolute;
  left: 260px;
  top: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
}

.sk-section {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 16px 1px rgb(0 0 0 / 6%);
}

.sk-line-title {
  width: 160px;
  height: 28px;
  border-radius: 6px;
  background: #e2e8f0;
  margin-bottom: 24px;
}

.sk-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
}

.sk-card {
  border-radius: 10px;
  overflow: hidden;
  background: #f8fafc;
}

.sk-card-cover {
  height: 210px;
  background: linear-gradient(135deg, #e2e8f0 0%, #f1f5f9 100%);
}

.sk-card-body {
  padding: 14px;
}

.sk-card-title {
  height: 50px;
  border-radius: 6px;
  background: #e2e8f0;
}

.sk-card-meta {
  margin-top: 12px;
  height: 18px;
  width: 60%;
  border-radius: 4px;
  background: #e2e8f0;
}

.hero-card {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;
}

.hero-carousel {
  border-radius: 0 12px 12px 0;
  overflow: hidden;
  margin-left: 260px;
  width: calc(100% - 260px);
}

.hero-slide {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  cursor: pointer;
}

.hero-slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.recommend-cover img,
.product-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.hero-slide-overlay {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 40px 32px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.55) 0%, transparent 100%);
  z-index: 3;
}

.hero-slide-title {
  color: #fff;
  font-size: 22px;
  font-weight: 600;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.hero-slide-price {
  margin-top: 10px;
  color: #fff;
  font-size: 24px;
  font-weight: bold;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}

.hero-slide-price em {
  font-style: normal;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  margin-left: 2px;
}

.hero-sidebar {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 260px;
  z-index: 5;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0.86) 100%);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
  padding: 0;
  border-right: 1px solid rgba(0, 0, 0, 0.06);
}

.sidebar-title {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 22px 22px 18px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  color: #1e293b;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.04em;
}

.sidebar-title i {
  font-size: 18px;
  color: #3b82f6;
}

.sidebar-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 22px;
  cursor: pointer;
  transition: all 0.25s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.04);
  position: relative;
}

.sidebar-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #3b82f6;
  border-radius: 0 3px 3px 0;
  opacity: 0;
  transform: scaleY(0);
  transition: all 0.25s ease;
}

.sidebar-item:hover {
  background: rgba(59, 130, 246, 0.06);
}

.sidebar-item:hover::before {
  opacity: 1;
  transform: scaleY(1);
}

.sidebar-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: #f1f5f9;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background 0.25s ease;
}

.sidebar-item:hover .sidebar-icon {
  background: #dbeafe;
}

.sidebar-icon img {
  width: 22px;
  height: 22px;
  object-fit: contain;
}

.sidebar-name {
  flex: 1;
  min-width: 0;
  color: #334155;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s ease;
}

.sidebar-item:hover .sidebar-name {
  color: #1e40af;
}

.sidebar-arrow {
  font-size: 12px;
  color: #cbd5e1;
  transition: all 0.25s ease;
}

.sidebar-item:hover .sidebar-arrow {
  color: #3b82f6;
  transform: translateX(3px);
}

.section-box {
  margin-top: 20px;
  padding: 24px;
}

.line-title {
  margin-bottom: 20px;
}

.title-container .chinese-title {
  font-size: 24px;
  color: #333;
  font-weight: bold;
}

.title-container .english-title {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
}

.recommend-grid,
.product-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
}

.recommend-card,
.product-card {
  border: 1px solid #edf1f7;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
}

.recommend-cover,
.product-cover {
  height: 210px;
  background: #f3f6fb;
}

.recommend-body,
.product-body {
  padding: 14px;
}

.recommend-name,
.product-name {
  color: #333;
  font-size: 15px;
  font-weight: bold;
  line-height: 1.7;
  min-height: 50px;
}

.recommend-meta,
.product-meta {
  margin-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.score {
  color: #f59a23;
  font-size: 13px;
}

.price {
  color: #1c5098;
  font-size: 22px;
  font-weight: bold;
}

.price em {
  font-style: normal;
  font-size: 12px;
  color: #999;
  margin-left: 2px;
}

.shop-body {
  margin-top: 20px;
  display: flex;
  align-items: flex-start;
}

.left-nav {
  width: 220px;
  margin-right: 20px;
}

.left-nav-inner {
  padding: 20px 0;
  position: sticky;
  top: 20px;
}

.left-nav-title {
  padding: 0 20px 14px;
  color: #333;
  font-size: 20px;
  font-weight: bold;
  border-bottom: 1px solid #edf1f7;
}

.left-nav-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  color: #555;
  cursor: pointer;
  border-bottom: 1px solid #f5f7fb;
}

.left-nav-item i {
  font-style: normal;
  color: #999;
}

.right-content {
  flex: 1;
}

.category-section {
  padding: 24px;
  margin-bottom: 20px;
}

.category-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.category-title {
  color: #333;
  font-size: 24px;
  font-weight: bold;
}

.filter-toggle {
  color: #1c5098;
  cursor: pointer;
  font-size: 14px;
}

.child-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin: 18px 0 20px;
}

.child-tab {
  padding: 9px 18px;
  border-radius: 18px;
  background: #f5f7fb;
  color: #666;
  cursor: pointer;
  font-size: 13px;
}

.child-tab.active {
  background: #1c5098;
  color: #fff;
}

.inner-loading {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #1c5098;
}

.error-box {
  margin-top: 20px;
}

/* ===== Page Load Staggered Fade-in ===== */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.shop-wrap > .hero-card {
  animation: fadeInUp 0.5s ease both;
}

.shop-wrap > .section-box {
  animation: fadeInUp 0.5s ease 0.1s both;
}

.shop-wrap > .shop-body {
  animation: fadeInUp 0.5s ease 0.2s both;
}

/* ===== Ken Burns Effect for Hero Slides ===== */
@keyframes kenBurns {
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(1.05);
  }
}

.hero-slide img {
  animation: kenBurns 5s ease-out forwards;
}

/* ===== Card Hover Effects ===== */
.recommend-card,
.product-card {
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.recommend-card:hover,
.product-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}

.recommend-cover img,
.product-cover img {
  transition: transform 0.4s ease;
}

.recommend-card:hover .recommend-cover img,
.product-card:hover .product-cover img {
  transform: scale(1.08);
}

.recommend-card:hover .price,
.product-card:hover .price {
  color: #0f3d7a;
  transform: scale(1.06);
}

.price {
  transition: all 0.3s ease;
  display: inline-block;
}

/* ===== Score Hover ===== */
.score {
  transition: all 0.25s ease;
  display: inline-block;
}

.recommend-card:hover .score,
.product-card:hover .score {
  transform: scale(1.15);
  color: #fbbf24;
}

/* ===== Title Decoration Line ===== */
.title-container .chinese-title {
  position: relative;
  padding-left: 18px;
  transition: color 0.3s ease;
}

.title-container .chinese-title::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%) scaleY(0.6);
  width: 4px;
  height: 60%;
  background: #3b82f6;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.title-container .chinese-title:hover {
  color: #1e40af;
}

.title-container .chinese-title:hover::before {
  transform: translateY(-50%) scaleY(1);
  height: 80%;
  background: #1e40af;
}

/* ===== Left Nav Item Hover ===== */
.left-nav-item {
  position: relative;
  transition: all 0.25s ease;
}

.left-nav-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #3b82f6;
  border-radius: 0 3px 3px 0;
  opacity: 0;
  transform: scaleY(0);
  transition: all 0.25s ease;
}

.left-nav-item:hover {
  background: rgba(59, 130, 246, 0.06);
  color: #1e40af;
}

.left-nav-item:hover::before {
  opacity: 1;
  transform: scaleY(1);
}

.left-nav-item i {
  transition: all 0.25s ease;
}

.left-nav-item:hover i {
  color: #3b82f6;
  transform: scale(1.1);
}

/* ===== Child Tab Transitions ===== */
.child-tab {
  transition: all 0.25s ease;
}

.child-tab:hover {
  background: #e8edf5;
  color: #1c5098;
}

.child-tab.active {
  transform: scale(1.05);
}

/* ===== Filter Toggle Underline ===== */
.filter-toggle {
  position: relative;
  transition: color 0.2s ease;
}

.filter-toggle::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 0;
  height: 2px;
  background: #1c5098;
  transition: width 0.3s ease;
}

.filter-toggle:hover {
  color: #1e40af;
}

.filter-toggle:hover::after {
  width: 100%;
}

@media (max-width: 1260px) {
  .shop-wrap {
    width: 96%;
  }

  .hero-sidebar {
    width: 220px;
  }

  .hero-carousel {
    margin-left: 220px;
    width: calc(100% - 220px);
  }

  .sidebar-item {
    padding: 16px 18px;
  }

  .sk-sidebar {
    width: 220px;
  }

  .sk-banner {
    left: 220px;
  }

  .sk-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .recommend-grid,
  .product-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 900px) {
  .shop-body {
    display: block;
  }

  .left-nav,
  .right-content {
    width: 100%;
  }

  .hero-carousel /deep/ .el-carousel__container {
    height: 100% !important;
  }

  .hero-carousel {
    margin-left: 0;
    width: 100%;
  }

  .sk-hero {
    height: 320px;
    flex-direction: column;
  }

  .sk-sidebar {
    position: static;
    width: 100%;
    height: auto;
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0;
  }

  .sk-sidebar-title {
    grid-column: 1 / -1;
    margin: 0 20px;
  }

  .sk-banner {
    position: static;
    height: 160px;
  }

  .hero-sidebar {
    position: static;
    width: 100%;
    background: rgba(10, 18, 36, 0.88);
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0;
  }

  .sidebar-title {
    grid-column: 1 / -1;
    padding: 16px 20px 12px;
    font-size: 15px;
  }

  .sidebar-item {
    padding: 14px 20px;
    border-right: 1px solid rgba(255, 255, 255, 0.06);
  }

  .sk-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .recommend-grid,
  .product-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .left-nav {
    margin-right: 0;
    margin-bottom: 20px;
  }

  .left-nav-inner {
    position: static;
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/shop/index.vue